/**
 * "Mi imputación": the person's month against Redmine, and logging it there
 * with their own login.
 *
 * With `userId` it is the admin's read-only view of someone else's month (no
 * inputs, no settings, no submit bar), fed by GET /imputacion/users/{id}.
 *
 * Submitting sends one ISO week per request, in sequence, then reads the month
 * again and keeps in the draft only what did not go through. A request that
 * gets no answer is never sent again: the month is re-read instead, because
 * Redmine may have saved it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { isAxiosError } from 'axios'
import { api } from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import type { ImputacionMonth, IsoDate, SubmitCell, SubmitResult } from '../../../types/imputacion'
import {
    type Draft,
    type SeenTotals,
    POST_LOGIN_REDIRECT_KEY,
    applyCellChange,
    blockingLines,
    chunkByWeek,
    conflictDays,
    dayFillHours,
    draftIssues,
    draftStats,
    draftStorage,
    dropSettled,
    fillableDays,
    findDay,
    fmtH,
    isDraftEmpty,
    mergeAfterSubmit,
    monthName,
    pendingDays,
    pruneOrphans,
    rebaseSeen,
    resultsByCell,
    round2,
    seenDayTotals,
    shiftMonth,
    sourceLabel,
    toSubmitCells,
    todayIncludedByDefault,
} from './imputacionLogic'
import { ImputacionSummaryCards } from './ImputacionSummaryCards'
import { LineSettingsStrip } from './LineSettingsStrip'
import { ImputacionGrid } from './ImputacionGrid'
import { cellDomId } from './DayCardList'
import { FillConfirmModal } from './FillConfirmModal'
import { SubmitBar, type SubmitProgress } from './SubmitBar'
import { SubmitResultPanel, type SubmitSummary, UNKNOWN_OUTCOME_MESSAGE } from './SubmitResultPanel'

export interface ImputacionPanelProps {
    /** Someone else's month (admin, read-only). */
    userId?: number
    readOnly?: boolean
    initialYear?: number
    initialMonth?: number
}

// ---------------------------------------------------------------------------
// Styles (tokens as in the old Profile › Mi Imputación table)
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.primary};
`

const MonthSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;

  button {
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: white;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    width: 30px;
    height: 30px;
    cursor: pointer;
    font-size: 1rem;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const ConnChip = styled.span<{ $ok: boolean }>`
  padding: 3px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  background: ${({ $ok }) => ($ok ? '#E8F5E9' : '#EEEEEE')};
  color: ${({ $ok }) => ($ok ? '#2E7D32' : '#555')};
`

const Banner = styled.div<{ $tone: 'info' | 'warn' | 'bad' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-left: 4px solid ${({ $tone }) => ({ info: '#1976D2', warn: '#FF9800', bad: '#C62828' })[$tone]};
  background: ${({ $tone }) => ({ info: '#E3F2FD', warn: '#FFF8E1', bad: '#FFEBEE' })[$tone]};
`

const BannerButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 4px 10px;
  font-weight: 600;
  cursor: pointer;
`

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const PrimaryButton = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Hints = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};

  button {
    border: none;
    background: none;
    padding: 0;
    color: #1565C0;
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
  }
`

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`

const Skeleton = styled.div`
  display: grid;
  grid-template-columns: 160px repeat(10, 1fr);
  gap: 6px;

  span {
    height: 26px;
    border-radius: 4px;
    background: #ECEFF1;
    animation: ${pulse} 1.2s ease-in-out infinite;
  }
`

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const errorDetail = (e: unknown): { status?: number; code?: string; message?: string } => {
    if (!isAxiosError(e) || !e.response) return {}
    const detail = (e.response.data as { detail?: unknown } | undefined)?.detail
    if (detail && typeof detail === 'object') {
        const d = detail as { code?: string; message?: string }
        return { status: e.response.status, code: d.code, message: d.message }
    }
    return { status: e.response.status, message: typeof detail === 'string' ? detail : undefined }
}

/** No answer at all (timeout, connection lost): Redmine may or may not have saved it. */
const isNoAnswer = (e: unknown) => isAxiosError(e) && !e.response

const monthKey = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`

export function ImputacionPanel({ userId, readOnly = false, initialYear, initialMonth }: ImputacionPanelProps) {
    const { user, logout } = useAuth()
    const navigate = useNavigate()
    const viewingOther = userId !== undefined && userId !== null
    const ro = readOnly || viewingOther
    const uid = user?.id ?? 0
    const isAdmin = user?.role?.toLowerCase() === 'admin'

    const [data, setData] = useState<ImputacionMonth | null>(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [draft, setDraftState] = useState<Draft>({})
    const [restored, setRestored] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [progress, setProgress] = useState<SubmitProgress | null>(null)
    const [status, setStatus] = useState<string | null>(null)
    const [results, setResults] = useState<SubmitResult[]>([])
    const [summary, setSummary] = useState<SubmitSummary | null>(null)
    const [loginRequired, setLoginRequired] = useState(false)
    const [sessionExpired, setSessionExpired] = useState(false)
    const [notice, setNotice] = useState<{ text: string; reload?: boolean } | null>(null)
    const [fill, setFill] = useState<{ days: IsoDate[]; checked: IsoDate[] } | null>(null)
    const [highlightDay, setHighlightDay] = useState<IsoDate | null>(null)

    // Refs: the submit loop and the storage writes must see the latest values,
    // not the ones captured when a callback was created.
    const dataRef = useRef<ImputacionMonth | null>(null)
    const draftRef = useRef<Draft>({})
    // The day totals the draft cells were typed against (sent as seen_day_totals,
    // persisted with the draft), so a re-read in between cannot hide an edit
    // made in Redmine meanwhile from the server's day guard.
    const seenRef = useRef<SeenTotals>({})
    const submittingRef = useRef(false)
    const requestRef = useRef(0)
    const wantedRef = useRef<{ year?: number; month?: number }>({ year: initialYear, month: initialMonth })

    const setMonthData = (d: ImputacionMonth) => {
        dataRef.current = d
        setData(d)
    }

    /** Every draft change goes through here so sessionStorage always holds the current draft of this month. */
    const updateDraft = useCallback(
        (next: Draft) => {
            draftRef.current = next
            setDraftState(next)
            const d = dataRef.current
            if (d) seenRef.current = rebaseSeen(d, next, seenRef.current)
            if (!ro && uid && d) draftStorage.save(uid, d.year, d.month, next, seenRef.current)
        },
        [ro, uid],
    )

    const fetchMonth = useCallback(
        async (year?: number, month?: number): Promise<ImputacionMonth> => {
            const resp = viewingOther
                ? await api.imputacion.getUser(userId as number, year, month)
                : await api.imputacion.getMine(year, month)
            return resp.data
        },
        [viewingOther, userId],
    )

    const load = useCallback(
        async (year?: number, month?: number) => {
            // Never switch months under a running submit: its re-read and draft
            // merge belong to the month it started on.
            if (submittingRef.current) return
            const req = ++requestRef.current
            wantedRef.current = { year, month }
            setLoading(true)
            setLoadError(false)
            try {
                const d = await fetchMonth(year, month)
                if (req !== requestRef.current) return
                setMonthData(d)
                setResults([])
                setSummary(null)
                setNotice(null)
                setHighlightDay(null)
                const saved = !ro && uid ? draftStorage.loadEntry(uid, d.year, d.month) : null
                draftRef.current = saved?.draft ?? {}
                seenRef.current = saved ? rebaseSeen(d, saved.draft, saved.seen) : {}
                setDraftState(saved?.draft ?? {})
                setRestored(Boolean(saved))
            } catch (e) {
                if (req !== requestRef.current) return
                if (errorDetail(e).status === 401 && !viewingOther) setSessionExpired(true)
                else setLoadError(true)
            } finally {
                if (req === requestRef.current) setLoading(false)
            }
        },
        [fetchMonth, ro, uid, viewingOther],
    )

    useEffect(() => {
        load(initialYear, initialMonth)
    }, [load, initialYear, initialMonth])

    /** Re-reads the shown month without touching the draft. */
    const refetch = useCallback(async (): Promise<ImputacionMonth | null> => {
        const d = dataRef.current
        if (!d) return null
        const fresh = await fetchMonth(d.year, d.month)
        setMonthData(fresh)
        return fresh
    }, [fetchMonth])

    // ---- Login -----------------------------------------------------------

    const goLogin = async () => {
        const d = dataRef.current
        const year = d?.year ?? wantedRef.current.year
        const month = d?.month ?? wantedRef.current.month
        if (uid && d) draftStorage.save(uid, d.year, d.month, draftRef.current, seenRef.current)
        try {
            const target = year && month ? `/imputacion?year=${year}&month=${month}&restore=1` : '/imputacion?restore=1'
            window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target)
        } catch {
            // Without storage the person lands on the default page after login; the draft is lost only then.
        }
        await logout()
        navigate('/login')
    }

    // ---- Cells and choices -------------------------------------------------

    const onCellChange = useCallback(
        (lineKey: string, iso: IsoDate, value: number | null) => {
            const d = dataRef.current
            if (!d) return
            updateDraft(applyCellChange(d, draftRef.current, lineKey, iso, value))
        },
        [updateDraft],
    )

    const onChoose = async (rowKey: string, projectId: number | null, activityId: number | null) => {
        try {
            await api.imputacion.setRowChoice({ row_key: rowKey, redmine_project_id: projectId, activity_id: activityId })
        } catch (e) {
            const { status: st, code, message } = errorDetail(e)
            if (st === 401) {
                setSessionExpired(true)
                throw new Error('Tu sesión ha caducado.')
            }
            if (st === 409 && code === 'redmine_login_required') {
                setLoginRequired(true)
                throw new Error('Inicia sesión de nuevo para elegir el proyecto.')
            }
            throw new Error(message ?? 'No se pudo guardar la elección.')
        }
        try {
            const fresh = await refetch()
            // A new project can move a line (its key is the project): cells left on
            // a line that no longer exists would never be sent but keep the bar open.
            if (fresh?.can_edit) updateDraft(pruneOrphans(fresh, draftRef.current))
        } catch {
            setLoadError(true)
        }
    }

    // ---- Submit ------------------------------------------------------------

    const submit = useCallback(
        async (override?: Draft) => {
            const d = dataRef.current
            if (!d || submittingRef.current) return
            const toSend = override ?? draftRef.current
            const cells = toSubmitCells(d, toSend)
            if (!cells.length || draftIssues(d, toSend).length) return
            // The ref, not the state, guards a double click: state updates land after the second click.
            submittingRef.current = true
            setSubmitting(true)
            setResults([])
            setSummary(null)
            setNotice(null)
            setStatus(null)

            const chunks = chunkByWeek(cells)
            const all: SubmitResult[] = []
            const counts = { created: 0, updated: 0, deleted: 0, unchanged: 0 }
            let saved = 0
            let stopped: 'auth' | 'uncertain' | null = null
            let lost: SubmitCell[] | null = null
            let expired = false
            try {
                for (let i = 0; i < chunks.length; i++) {
                    setProgress({ chunk: i + 1, chunks: chunks.length, savedHours: round2(saved) })
                    try {
                        const resp = await api.imputacion.submit({
                            year: d.year,
                            month: d.month,
                            cells: chunks[i],
                            seen_day_totals: seenDayTotals(d, chunks[i], seenRef.current),
                            layout_key: d.layout_key ?? null,
                        })
                        const r = resp.data
                        all.push(...r.results)
                        counts.created += r.created
                        counts.updated += r.updated
                        counts.deleted += r.deleted
                        counts.unchanged += r.unchanged
                        saved += r.hours_added
                        setProgress({ chunk: i + 1, chunks: chunks.length, savedHours: round2(saved) })
                        if (r.stopped_reason) {
                            stopped = r.stopped_reason
                            break
                        }
                    } catch (e) {
                        if (isNoAnswer(e)) {
                            lost = chunks[i]
                            break
                        }
                        const { status: st, code, message } = errorDetail(e)
                        if (st === 401) {
                            expired = true
                            setSessionExpired(true)
                        } else if (st === 409 && code === 'redmine_login_required') {
                            setLoginRequired(true)
                        } else if (st === 409 && code === 'submit_in_progress') {
                            setNotice({ text: 'Ya hay un envío en curso; espera unos segundos.' })
                        } else if (st === 409 && code === 'reload_required') {
                            setNotice({ text: 'Recarga la página antes de imputar (tu tabla ha cambiado)', reload: true })
                        } else if (st === 409 && code === 'imputacion_disabled') {
                            setNotice({
                                text: 'Imputar desde el Manager se activará en breve. De momento puedes ver tus horas y lo que te falta.',
                            })
                        } else if (st === 503) {
                            setNotice({ text: 'Redmine no está disponible; no se ha imputado nada.' })
                        } else {
                            setNotice({ text: message ?? 'No se pudo imputar. Inténtalo de nuevo.' })
                        }
                        break
                    }
                }

                if (stopped === 'auth') setLoginRequired(true)
                if (lost) setStatus('Comprobando qué se guardó…')

                let shown = all
                if (expired) {
                    // No re-read possible: drop what the server settled so a later
                    // resend does not trip the day guard on our own writes.
                    updateDraft(dropSettled(d, toSend, all))
                } else {
                    try {
                        const fresh = await refetch()
                        if (fresh) {
                            const next = mergeAfterSubmit(toSend, all, fresh)
                            if (lost) {
                                // Cells of the unanswered week that still differ: flag them, never resend on our own.
                                const flagged: SubmitResult[] = lost
                                    .filter((c) => {
                                        const line = fresh.lines.find((l) => l.redmine_project_id === c.project_id)
                                        return line ? next[line.key]?.[c.spent_on] !== undefined : false
                                    })
                                    .map((c) => ({
                                        ...c,
                                        status: 'unconfirmed' as const,
                                        entry_ids: [],
                                        logged_before: 0,
                                        logged_after: 0,
                                        message: UNKNOWN_OUTCOME_MESSAGE,
                                        errors: [],
                                    }))
                                shown = [...all, ...flagged]
                            }
                            // What is left is now compared against this re-read, which
                            // already holds this run's own writes. A read-only re-read (login
                            // lost) is the readers' view, whose totals can differ from the own
                            // read: the basis is kept then, and a resend on a day this run
                            // wrote comes back as a conflict, never as a second write.
                            if (fresh.can_edit) seenRef.current = {}
                            updateDraft(next)
                        }
                    } catch (e) {
                        updateDraft(dropSettled(d, toSend, all))
                        if (errorDetail(e).status === 401) setSessionExpired(true)
                        else setNotice({ text: 'No se pudo volver a leer tu imputación. Recarga antes de reenviar.', reload: true })
                    }
                }
                if (shown.length || stopped) {
                    setResults(shown)
                    setSummary({ ...counts, stoppedReason: stopped })
                }
            } finally {
                submittingRef.current = false
                setSubmitting(false)
                setProgress(null)
                setStatus(null)
            }
        },
        [refetch, updateDraft],
    )

    // ---- Fill --------------------------------------------------------------

    const openFill = (single?: IsoDate) => {
        const d = dataRef.current
        if (!d) return
        if (single) {
            setFill({ days: [single], checked: [single] })
            return
        }
        const days = fillableDays(d, true)
        const withToday = todayIncludedByDefault(d)
        setFill({ days, checked: days.filter((x) => findDay(d, x)?.state !== 'today' || withToday) })
    }

    const confirmFill = (fillDraft: Draft, send: boolean) => {
        const d = dataRef.current
        setFill(null)
        if (!d) return
        let next = draftRef.current
        // Absence cells last: a full absence clears the other drafts of its day.
        const entries = Object.entries(fillDraft).sort(
            ([a], [b]) =>
                Number(d.lines.find((l) => l.key === a)?.kind === 'absence') -
                Number(d.lines.find((l) => l.key === b)?.kind === 'absence'),
        )
        for (const [key, cells] of entries) {
            for (const [iso, value] of Object.entries(cells)) next = applyCellChange(d, next, key, iso, value)
        }
        updateDraft(next)
        if (send) void submit(next)
    }

    const goToCell = (lineKey: string, iso: IsoDate) => {
        setHighlightDay(iso)
        const el = document.getElementById(cellDomId(lineKey, iso))
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
        el?.focus?.()
    }

    // ---- Derived -----------------------------------------------------------

    const cellResults = useMemo(() => (data ? resultsByCell(data, results) : {}), [data, results])
    const conflicts = useMemo(() => conflictDays(results), [results])

    // ---- Render ------------------------------------------------------------

    const wanted = wantedRef.current
    if (loading && !data) {
        return (
            <Wrap>
                {!ro && <Title>Mi imputación</Title>}
                {sessionExpired && <SessionExpiredBanner onLogin={goLogin} />}
                <Skeleton role="status" aria-label="Cargando imputación">
                    {Array.from({ length: 44 }, (_, i) => (
                        <span key={i} />
                    ))}
                </Skeleton>
            </Wrap>
        )
    }

    if (!data) {
        return (
            <Wrap>
                {!ro && <Title>Mi imputación</Title>}
                {sessionExpired ? (
                    <SessionExpiredBanner onLogin={goLogin} />
                ) : (
                    <Empty>
                        <div>No se pudo cargar tu imputación</div>
                        <BannerButton type="button" onClick={() => load(wanted.year, wanted.month)}>
                            Reintentar
                        </BannerButton>
                    </Empty>
                )}
            </Wrap>
        )
    }

    const todayMonth = data.today.slice(0, 7)
    const shownMonth = monthKey(data.year, data.month)
    const isCurrentMonth = shownMonth === todayMonth
    const canGoForward = shownMonth < todayMonth
    const needsLogin = !ro && (loginRequired || data.login_required)
    const editableView = !ro && data.month_editable
    const stats = draftStats(data, draft)
    const issues = draftIssues(data, draft)

    // Primary action
    const candidates = editableView ? fillableDays(data, true) : []
    const withToday = todayIncludedByDefault(data)
    const defaultDays = candidates.filter((x) => findDay(data, x)?.state !== 'today' || withToday)
    const fillHours = round2(defaultDays.reduce((s, x) => s + dayFillHours(data, x), 0))
    const blocking = editableView ? blockingLines(data, pendingDays(data, true)) : []
    let fillDisabled: string | null = null
    if (!data.can_edit) fillDisabled = 'Solo lectura'
    else if (!data.bulk_fill_allowed) fillDisabled = 'Tu jornada no está configurada en el Manager; ajusta las horas a mano'
    else if (blocking.length) fillDisabled = `Elige en qué proyecto imputar «${sourceLabel(blocking[0])}»`
    else if (!candidates.length) fillDisabled = 'No tienes días pendientes ✓'
    const incomplete = data.days.filter(
        (x) => x.editable && x.state !== 'future' && (x.chip === 'parcial' || x.chip === 'vacio'),
    )
    const includesToday = defaultDays.some((x) => findDay(data, x)?.state === 'today')
    const prev = data.previous_month
    const noWorkingDays = !data.days.some((x) => x.kind === 'working')

    return (
        <Wrap>
            <HeaderRow>
                {!ro && <Title>Mi imputación</Title>}
                <MonthSwitch>
                    <button
                        type="button"
                        aria-label="Mes anterior"
                        disabled={submitting}
                        onClick={() => {
                            const t = shiftMonth(data.year, data.month, -1)
                            load(t.year, t.month)
                        }}
                    >
                        ‹
                    </button>
                    <span>
                        {monthName(data.month)} {data.year}
                    </span>
                    <button
                        type="button"
                        aria-label="Mes siguiente"
                        disabled={!canGoForward || submitting}
                        onClick={() => {
                            const t = shiftMonth(data.year, data.month, 1)
                            load(t.year, t.month)
                        }}
                    >
                        ›
                    </button>
                </MonthSwitch>
                {!ro &&
                    (data.can_edit && !needsLogin ? (
                        <ConnChip $ok>
                            {data.redmine_login ? `Conectado a Redmine como ${data.redmine_login}` : 'Conectado a Redmine'}
                        </ConnChip>
                    ) : (
                        <ConnChip $ok={false}>
                            {needsLogin ? 'Solo lectura · inicia sesión de nuevo para imputar' : 'Solo lectura'}
                        </ConnChip>
                    ))}
            </HeaderRow>

            {/* Banners, each only when it applies */}
            {!ro && isCurrentMonth && prev && prev.editable && prev.missing_to_date > 0.5 && (
                <Banner $tone="warn">
                    <span>
                        {monthName(prev.month)} sigue abierto: te faltan {fmtH(prev.missing_to_date)}
                    </span>
                    <BannerButton type="button" disabled={submitting} onClick={() => load(prev.year, prev.month)}>
                        Ir a {monthName(prev.month).toLowerCase()}
                    </BannerButton>
                </Banner>
            )}
            {sessionExpired && <SessionExpiredBanner onLogin={goLogin} />}
            {needsLogin && !sessionExpired && (
                <Banner $tone="warn">
                    <span>
                        Para imputar desde aquí tienes que volver a iniciar sesión. Tus cambios se guardan en este navegador y
                        los recuperarás al volver.
                        {data.message ? ` (${data.message})` : ''}
                    </span>
                    <BannerButton type="button" onClick={goLogin}>
                        Iniciar sesión
                    </BannerButton>
                </Banner>
            )}
            {!ro && !data.writes_enabled && (
                <Banner $tone="info">
                    Imputar desde el Manager se activará en breve. De momento puedes ver tus horas y lo que te falta.
                </Banner>
            )}
            {!ro && data.own_read_failed && (
                <Banner $tone="warn">
                    No pudimos leer tus horas con tu usuario de Redmine; se muestra lo que ve el Manager.
                </Banner>
            )}
            {!data.redmine_available && (
                <Banner $tone="bad">Redmine no responde ahora mismo; se muestra solo la planificación.</Banner>
            )}
            {!ro && !data.month_editable && (
                <Banner $tone="info">
                    Este mes ya no se puede imputar desde el Manager; si falta algo, hazlo en Redmine.
                </Banner>
            )}
            {restored && !isDraftEmpty(draft) && (
                <Banner $tone="info">
                    <span>Hemos recuperado tus cambios sin enviar</span>
                    <BannerButton
                        type="button"
                        onClick={() => {
                            updateDraft({})
                            setRestored(false)
                        }}
                    >
                        Descartar
                    </BannerButton>
                </Banner>
            )}
            {notice && (
                <Banner $tone="bad" role="alert">
                    <span>{notice.text}</span>
                    {notice.reload && (
                        <BannerButton type="button" onClick={() => load(data.year, data.month)}>
                            Recargar
                        </BannerButton>
                    )}
                </Banner>
            )}
            {loadError && (
                <Banner $tone="bad">
                    <span>No se pudo cargar tu imputación</span>
                    <BannerButton type="button" onClick={() => load(wanted.year ?? data.year, wanted.month ?? data.month)}>
                        Reintentar
                    </BannerButton>
                </Banner>
            )}

            <ImputacionSummaryCards month={data} />

            {!ro && data.mode === 'own' && (
                <LineSettingsStrip month={data} isAdmin={isAdmin} onChoose={onChoose} disabled={submitting} />
            )}

            {editableView && (
                <ActionRow>
                    <PrimaryButton
                        type="button"
                        disabled={Boolean(fillDisabled) || submitting}
                        onClick={() => openFill()}
                    >
                        {fillDisabled || !defaultDays.length
                            ? 'Completar lo recomendado'
                            : `Completar lo recomendado (${defaultDays.length} ${defaultDays.length === 1 ? 'día' : 'días'} · ${fmtH(fillHours)})`}
                    </PrimaryButton>
                    <Hints>
                        {fillDisabled && <span>{fillDisabled}</span>}
                        {!fillDisabled && includesToday && <span>Incluye hoy</span>}
                        {!fillDisabled && !defaultDays.length && candidates.length > 0 && (
                            <span>Hoy se incluye a partir de las 15:00</span>
                        )}
                        {incomplete.length > 0 && (
                            <span>
                                {incomplete.length} {incomplete.length === 1 ? 'día incompleto' : 'días incompletos'} ·{' '}
                                <button type="button" onClick={() => setHighlightDay(incomplete[0].date)}>
                                    Ir al primero
                                </button>
                            </span>
                        )}
                    </Hints>
                </ActionRow>
            )}

            {summary && (
                <SubmitResultPanel
                    month={data}
                    results={results}
                    summary={summary}
                    onGoToCell={goToCell}
                    onClose={() => setSummary(null)}
                />
            )}

            {noWorkingDays ? (
                <Empty>No hay días laborables en {monthName(data.month).toLowerCase()}</Empty>
            ) : (
                <ImputacionGrid
                    month={data}
                    draft={draft}
                    readOnly={ro}
                    onCellChange={onCellChange}
                    onFillDay={(iso) => openFill(iso)}
                    results={cellResults}
                    conflictDays={conflicts}
                    highlightDay={highlightDay}
                    locked={submitting}
                />
            )}

            {!ro && data.can_edit && (!isDraftEmpty(draft) || submitting) && (
                <SubmitBar
                    changes={stats.changes}
                    added={stats.added}
                    removed={stats.removed}
                    submitting={submitting}
                    progress={progress}
                    status={status}
                    blockedReason={issues.length ? 'Corrige las celdas marcadas' : null}
                    onDiscard={() => {
                        updateDraft({})
                        setRestored(false)
                    }}
                    onSubmit={() => void submit()}
                />
            )}

            {fill && (
                <FillConfirmModal
                    month={data}
                    days={fill.days}
                    initiallyChecked={fill.checked}
                    onConfirm={confirmFill}
                    onClose={() => setFill(null)}
                />
            )}
        </Wrap>
    )
}

function SessionExpiredBanner({ onLogin }: { onLogin: () => void }) {
    return (
        <Banner $tone="warn" role="alert">
            <span>Tu sesión ha caducado. Inicia sesión de nuevo; no perderás lo que has editado.</span>
            <BannerButton type="button" onClick={onLogin}>
                Iniciar sesión
            </BannerButton>
        </Banner>
    )
}
